import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { MagnifyingGlass, MapTrifold, ListBullets, X } from "@phosphor-icons/react";
import { useT } from "../i18n";
import { useRate } from "../context/RateContext";
import PropertyCard from "../components/PropertyCard";
import api from "../lib/api";
import { formatMAD, convertMad } from "../lib/format";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TYPES = ["all", "residence", "individual", "commercial", "office", "logistics"];

const yIcon = (active) =>
  L.divIcon({
    className: "",
    html: `<div style="width:${active ? 40 : 26}px;height:${active ? 40 : 26}px;background:${active ? "#E0A85A" : "#1A3A5C"};border:2px solid #fff;border-radius:9999px;box-shadow:0 2px 8px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;color:#fff;font-family:'Playfair Display',serif;font-weight:700;font-size:${active ? 20 : 13}px">Y</div>`,
    iconSize: [active ? 40 : 26, active ? 40 : 26],
    iconAnchor: [active ? 20 : 13, active ? 20 : 13],
  });

const MapController = ({ active, properties }) => {
  const map = useMap();
  useEffect(() => {
    const p = properties.find((x) => x.id === active);
    if (p && p.lat != null) map.panTo([p.lat, p.lng], { animate: true, duration: 0.6 });
  }, [active, properties, map]);
  return null;
};

const ClusterMarkers = ({ properties, onSelect, navigate, lang, t, rate }) => {
  const map = useMap();
  useEffect(() => {
    const group = L.markerClusterGroup({
      maxClusterRadius: 48,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) =>
        L.divIcon({ className: "", html: `<div class="marker-cluster-y">${cluster.getChildCount()}</div>`, iconSize: [44, 44], iconAnchor: [22, 22] }),
    });
    properties.forEach((p) => {
      if (p.lat == null) return;
      const m = L.marker([p.lat, p.lng], { icon: yIcon(false) });
      const conv = p.price_on_request || !p.price_mad ? null : convertMad(p.price_mad, rate, lang);
      const price = p.price_on_request || !p.price_mad
        ? `<div style="font-weight:700;color:#1A3A5C;margin-top:4px">${t("prop.on_request")}</div>`
        : `<div style="font-weight:700;color:#1A3A5C;margin-top:2px">${formatMAD(p.price_mad, lang)} MAD</div><div style="font-size:12px;color:#E0A85A">≈ ${conv.text} ${conv.symbol}</div>`;
      const title = (p.title?.[lang] || p.title?.fr || "").replace(/"/g, "&quot;");
      m.bindPopup(
        `<div style="width:210px" data-testid="map-popup-${p.id}"><img src="${p.images?.[0]}" style="width:100%;height:110px;object-fit:cover;border-radius:8px" /><div style="font-weight:700;color:#1A3A5C;margin-top:6px">${title}</div><div style="font-size:12px;color:#6b7280">${p.city} · ${t(`types.${p.type}`)}</div>${price}<button class="popup-view" data-id="${p.id}" style="margin-top:8px;width:100%;background:#1A3A5C;color:#fff;padding:8px 0;border-radius:9999px;font-weight:600;border:none;cursor:pointer">${t("prop.view_all_project") ? "Voir la fiche" : "Voir"}</button></div>`
      );
      m.on("click", () => onSelect(p.id));
      group.addLayer(m);
    });
    map.addLayer(group);
    const onOpen = (e) => {
      const btn = e.popup?._contentNode?.querySelector(".popup-view");
      if (btn) btn.onclick = () => navigate(`/biens/${btn.dataset.id}`);
    };
    map.on("popupopen", onOpen);
    return () => { map.off("popupopen", onOpen); map.removeLayer(group); };
  }, [properties, map, lang]);
  return null;
};

const Catalog = () => {
  const { t, lang } = useT();
  const rate = useRate();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [showMapMobile, setShowMapMobile] = useState(false);
  const cardRefs = useRef({});

  const type = params.get("type") || "all";
  const city = params.get("city") || "all";
  const project = params.get("project") || "all";
  const sort = params.get("sort") || "recent";
  const [search, setSearch] = useState(params.get("search") || "");

  useEffect(() => { api.get("/properties/cities").then(({ data }) => setCities(data)).catch(() => {}); }, []);

  const fetchProps = useCallback(() => {
    setLoading(true);
    const q = {};
    if (type !== "all") q.type = type;
    if (city !== "all") q.city = city;
    if (project !== "all") q.project = project;
    if (sort) q.sort = sort;
    if (search) q.search = search;
    api.get("/properties", { params: q }).then(({ data }) => { setProperties(data); setActive(data[0]?.id || null); }).finally(() => setLoading(false));
  }, [type, city, project, sort, search]);

  useEffect(() => { const id = setTimeout(fetchProps, 250); return () => clearTimeout(id); }, [fetchProps]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value); else next.delete(key);
    setParams(next);
  };

  // sync scroll → active
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.dataset.id);
      },
      { root: null, rootMargin: "-40% 0px -40% 0px", threshold: [0.1, 0.5, 1] }
    );
    Object.values(cardRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [properties]);

  const withGeo = properties.filter((p) => p.lat != null);
  const center = withGeo.length ? [withGeo[0].lat, withGeo[0].lng] : [31.79, -7.09];

  return (
    <div className="relative z-10 pt-[72px]">
      {/* Filters */}
      <div className="sticky top-[72px] z-30 bg-white/95 backdrop-blur-xl border-b border-navy/10">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-3.5 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="catalog-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("catalog.search")} className="ps-10 h-11 rounded-lg border-navy/15" />
          </div>
          <div className="grid grid-cols-3 gap-3 lg:flex">
            <Select value={type} onValueChange={(v) => update("type", v)}>
              <SelectTrigger data-testid="catalog-filter-type" className="h-11 rounded-lg border-navy/15 lg:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`types.${tp}`)}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={city} onValueChange={(v) => update("city", v)}>
              <SelectTrigger data-testid="catalog-filter-city" className="h-11 rounded-lg border-navy/15 lg:w-40"><SelectValue placeholder={t("catalog.filter_city")} /></SelectTrigger>
              <SelectContent><SelectItem value="all">{t("catalog.all_cities")}</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => update("sort", v)}>
              <SelectTrigger data-testid="catalog-filter-sort" className="h-11 rounded-lg border-navy/15 lg:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">{t("catalog.sort_recent")}</SelectItem>
                <SelectItem value="price_asc">{t("catalog.sort_price_asc")}</SelectItem>
                <SelectItem value="price_desc">{t("catalog.sort_price_desc")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Split view */}
      <div className="max-w-[1600px] mx-auto lg:grid lg:grid-cols-2">
        {/* List */}
        <div className={`px-5 sm:px-8 py-6 ${showMapMobile ? "hidden lg:block" : "block"}`}>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="text-sm text-muted-foreground" data-testid="catalog-results-count">{properties.length} {t("catalog.results")}</div>
            {project !== "all" && properties[0]?.project_name && (
              <button data-testid="clear-project-filter" onClick={() => update("project", "all")} className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-navy border border-gold/40 rounded-full px-3 py-1 font-semibold">
                {properties[0].project_name?.[lang] || properties[0].project_name?.fr} <X size={13} />
              </button>
            )}
          </div>
          {loading ? (
            <div className="text-center py-20 text-muted-foreground">{t("common.loading")}</div>
          ) : properties.length === 0 ? (
            <div data-testid="catalog-empty" className="text-center py-20 text-muted-foreground">{t("catalog.empty")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {properties.map((p) => (
                <div key={p.id} data-id={p.id} ref={(el) => (cardRefs.current[p.id] = el)} onMouseEnter={() => setActive(p.id)}
                  className={`transition-all rounded-2xl ${active === p.id ? "ring-2 ring-gold ring-offset-2" : ""}`}>
                  <PropertyCard property={p} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className={`lg:sticky lg:top-[136px] lg:h-[calc(100vh-136px)] ${showMapMobile ? "block h-[calc(100vh-136px)]" : "hidden lg:block"}`}>
          {withGeo.length > 0 && (
            <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapController active={active} properties={withGeo} />
              <ClusterMarkers properties={withGeo} onSelect={setActive} navigate={navigate} lang={lang} t={t} rate={rate} />
            </MapContainer>
          )}
        </div>
      </div>

      {/* Mobile toggle */}
      <button data-testid="map-toggle" onClick={() => setShowMapMobile((v) => !v)} className="lg:hidden fixed bottom-24 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 bg-navy text-white px-5 py-3 rounded-full shadow-xl font-semibold text-sm">
        {showMapMobile ? <><ListBullets size={18} /> Liste</> : <><MapTrifold size={18} /> Carte</>}
      </button>
    </div>
  );
};

export default Catalog;
